import { type FC, useContext, useRef, useCallback } from 'react';
import { useFragment, usePaginationFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { NftItemTile } from 'dibs-nft-item-tile/exports/NftItemTile.ts';
import { useCurrencyStore } from 'dibs-buyer-layout/exports/currencyStore.ts';
import { NftHeartPillProvider } from 'dibs-nft-heart-pill/exports/NftHeartPillProvider.ts';
import { ServerVarsContext } from '../../utils/ServerVarsContext.tsx';
import { type RelayNode } from 'dibs-ts-utils/exports/RelayNode.ts';
import { Button } from 'dibs-elements/exports/Button.tsx';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';
import { trackEvent, trackEcommerce, trackingConstants, eventNameConstants } from 'dibs-tracking';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import {
    type NftPdpCreatorItems_creatorAddress$data,
    type NftPdpCreatorItems_creatorAddress$key,
} from './__generated__/NftPdpCreatorItems_creatorAddress.graphql.ts';
import { type NftPdpCreatorItems_viewer$key } from './__generated__/NftPdpCreatorItems_viewer.graphql.ts';
import styles from './NftPdpCreatorItems.scss';

type Props = {
    creatorAddress: NftPdpCreatorItems_creatorAddress$key | null | undefined;
    viewer: NftPdpCreatorItems_viewer$key;
};

export const NftPdpCreatorItems: FC<Props> = ({
    creatorAddress: creatorAddressRef,
    viewer: viewerRef,
}) => {
    const viewer = useFragment(
        graphql`
            fragment NftPdpCreatorItems_viewer on Viewer {
                ...NftItemTile_viewer
                ...NftHeartPillProvider_viewer
            }
        `,
        viewerRef
    );
    const {
        data: creatorAddress,
        hasNext,
        isLoadingNext,
        loadNext,
    } = usePaginationFragment(
        graphql`
            fragment NftPdpCreatorItems_creatorAddress on EthereumAddressType
            @refetchable(queryName: "NftPdpCreatorItemsRefetchQuery")
            @argumentDefinitions(itemCount: { type: "Int" }, after: { type: "String" }) {
                address
                publicProfile {
                    profileName
                }
                createdItems(first: $itemCount, after: $after, excludeItemIds: [$nftId])
                    @connection(key: "NftCmsCreator_createdItems") {
                    edges {
                        node {
                            serviceId
                            metadata {
                                ... on NftDigitalArtMetadata {
                                    name
                                }
                            }
                            mostRecentAuction {
                                serviceId
                                ...NftItemTile_nftAuction
                            }
                            ...NftItemTile_nftItem
                        }
                    }
                }
            }
        `,
        creatorAddressRef
    );
    const { isMobile } = useContext(ServerVarsContext);
    const { currency } = useCurrencyStore();
    const sectionRef = useRef<HTMLDivElement | null>(null);
    const hasFiredImpressionTrackingRef = useRef(false);

    const { ECOM_PRODUCT_CLICK, ECOM_PRODUCT_IMPRESSION } = trackingConstants;
    const { EVENT_SELECT_ITEM, EVENT_VIEW_ITEM_LIST } = eventNameConstants;

    const createdItems: NonNullable<
        RelayNode<NonNullable<NftPdpCreatorItems_creatorAddress$data['createdItems']>>
    >[] = [];
    for (const edge of creatorAddress?.createdItems?.edges || []) {
        if (edge?.node) {
            createdItems.push(edge.node);
        }
    }

    const profileName = creatorAddress?.publicProfile?.profileName;

    const getProductImpressionsList = useCallback(() => {
        return createdItems
            .map((item, i) => {
                if (!item) {
                    return null;
                }
                const { metadata, serviceId: itemId } = item;
                return {
                    name: metadata?.name,
                    id: itemId,
                    category: '/nft/art/',
                    variant: 'NFT',
                    position: i + 1,
                };
            })
            .filter(filterNulls);
    }, [createdItems]);

    if (!createdItems.length) {
        return null;
    }

    return (
        <>
            <VisibilityTracker
                elementRef={sectionRef}
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        trackEvent({
                            event: 'e_ga_event',
                            action: 'banner displayed',
                            category: 'promo interaction',
                            label: 'creator item list',
                            noninteraction: 1,
                        });
                    }
                }}
            />
            <div className={styles.auctionSectionContainer} ref={sectionRef}>
                {!isMobile && (
                    <HeadingLevel>
                        {Header => (
                            <Header className={styles.header}>
                                <FormattedMessage
                                    id="nft.pdp.creatorItems.header"
                                    defaultMessage="More from {profileName}"
                                    values={{
                                        profileName: profileName || 'creator',
                                    }}
                                />
                            </Header>
                        )}
                    </HeadingLevel>
                )}
                <ul className={styles.auctionItems}>
                    <NftHeartPillProvider
                        viewer={viewer}
                        nftItemIds={createdItems.map(item => item.serviceId).filter(filterNulls)}
                    >
                        {createdItems.map((item, i) => {
                            if (!item) {
                                return null;
                            }
                            return (
                                <li key={item.serviceId || i.toString()}>
                                    <NftItemTile
                                        tileType="CREATOR_REGULAR"
                                        gaLabel="PDP-NFT"
                                        nftItem={item}
                                        nftAuction={item.mostRecentAuction}
                                        viewer={viewer}
                                        isMobile={!!isMobile}
                                        currency={currency}
                                        fireTracking={() => {
                                            trackEcommerce({
                                                type: ECOM_PRODUCT_CLICK,
                                                eventName: EVENT_SELECT_ITEM,
                                                actionField: {
                                                    list: 'pdp more from creator',
                                                },
                                                products: [
                                                    {
                                                        name: item?.metadata?.name,
                                                        id: item?.serviceId,
                                                        category: '/nft/art/',
                                                        variant: 'NFT',
                                                        position: i + 1,
                                                    },
                                                ],
                                            });
                                        }}
                                        fireImpressionTracking={() => {
                                            if (!hasFiredImpressionTrackingRef.current) {
                                                trackEcommerce({
                                                    type: ECOM_PRODUCT_IMPRESSION,
                                                    eventName: EVENT_VIEW_ITEM_LIST,
                                                    actionField: {
                                                        list: 'pdp more from creator',
                                                    },
                                                    products: getProductImpressionsList(),
                                                });
                                                hasFiredImpressionTrackingRef.current = true;
                                            }
                                        }}
                                    />
                                </li>
                            );
                        })}
                    </NftHeartPillProvider>
                </ul>
                {hasNext && (
                    <div className={styles.buttonWrapper}>
                        <Button
                            type="secondary"
                            size="medium"
                            fullWidth={!!isMobile}
                            disabled={isLoadingNext}
                            onClick={() => {
                                loadNext(4);
                            }}
                        >
                            <FormattedMessage
                                id="abf.NftPdpCreatorItems.viewMore"
                                defaultMessage="View More"
                            />
                        </Button>
                    </div>
                )}
            </div>
        </>
    );
};
